import { WidgetDataTopics, WidgetHostHandlers } from '@finxone-platform/shared/app-sdk';
import { ProfileStateModel } from '@finxone-platform/shared/sys-config-types';
import { Store, createSelector } from '@ngxs/store';
import { Observable, lastValueFrom } from 'rxjs';
import { GetProfile } from '../../../../lib/actions/user-profile.action';
import { PageContextState, PageContextStateModel } from '../../../../lib/state/page-context.state';
import { ProfileState } from '../../../../lib/state/user-profile.state';
import { toWidgetProfile } from './custom-widget-contract-mappers';

/**
 * Data topics exposed to custom widgets over the fx-widget protocol. Each topic
 * maps the Ngxs state onto its app-sdk contract shape inside the selector
 * projection, so get() and subscribe() both deliver contract payloads
 * (contracts.ts WidgetDataTopics), never raw state models:
 *
 * - 'profile'     -> WidgetProfile from ProfileStateModel.
 * - 'pageContext' -> Record<string, string> from
 *                    PageContextStateModel.pageContextData. Host-set state:
 *                    there is no fetch action, so get() simply returns the
 *                    current selections and subscribe() follows SetPageContext
 *                    dispatches.
 *
 * **Only two, and deliberately.** A topic delivers one payload once — `data.get`
 * takes no page parameter and the protocol has no shape for a second page to
 * arrive in. Every collection that used to live here (accounts, beneficiaries,
 * transactions, cards, savings…) was therefore serving one arbitrary page as if
 * it were the whole set, with no way for a widget to discover otherwise. They
 * moved to `api.call`, which takes parameters and can be asked again. What is
 * left is what was never a collection fetch.
 *
 * 'handoffs' and 'onboardingStatus' are also topics, but neither is served from
 * here: the hand-off bridge layers 'handoffs' on top of this map
 * (custom-widget-handoff.ts) because it is instance-scoped, and
 * 'onboardingStatus' is derived alongside the onboarding operations.
 *
 * `realtime:<name>` is the one open-ended family: it subscribes to the events a
 * backend function pushes through its `realtime.emit` capability. The platform
 * delivers them all under the single socket eventType `function.custom` with the
 * function's own event name inside the payload, and the topic suffix is that
 * name — so `sdk.data.subscribe('realtime:pot-updated', handler)` receives
 * `{ name: 'pot-updated', data }` pushes and nothing else. Push-only: `get()`
 * answers null because nothing is retained to get.
 */
interface TopicEntry {
  selector: (...state: any[]) => unknown;
  isEmpty(value: any): boolean;
  /** Mirrors the fetch findAndRefreshRequiredData dispatches for the topic. */
  refresh(store: Store): Observable<unknown> | undefined;
}

const profileSelector = createSelector(
  [ProfileState.getProfile],
  (profile: ProfileStateModel): WidgetDataTopics['profile'] => toWidgetProfile(profile),
);

const pageContextSelector = createSelector(
  [PageContextState.getPageContextState],
  (state: PageContextStateModel): WidgetDataTopics['pageContext'] => ({
    ...(state?.pageContextData ?? {}),
  }),
);

const DATA_TOPICS: Record<string, TopicEntry> = {
  profile: {
    selector: profileSelector,
    isEmpty: (profile) => !profile?.id,
    refresh: (store) => store.dispatch(new GetProfile()),
  },
  pageContext: {
    selector: pageContextSelector,
    // Host-set state (SetPageContext): nothing to fetch, so get() returns the
    // current selections as-is — an empty record simply means none were made.
    isEmpty: () => false,
    refresh: () => undefined,
  },
};

const REALTIME_TOPIC_PREFIX = 'realtime:';

/** The seam this bridge needs from SocketService, so specs stay dependency-light. */
export interface FunctionEventSource {
  onFunctionEvent(name: string, listener: (data: Record<string, unknown>) => void): () => void;
}

export function buildCustomWidgetDataTopics(
  store: Store,
  functionEvents?: FunctionEventSource,
): NonNullable<WidgetHostHandlers['data']> {
  function realtimeName(topic: string): string | undefined {
    if (!topic.startsWith(REALTIME_TOPIC_PREFIX)) return undefined;
    const name = topic.slice(REALTIME_TOPIC_PREFIX.length);
    return name.length > 0 && functionEvents ? name : undefined;
  }

  function entryFor(topic: string): TopicEntry {
    const entry = DATA_TOPICS[topic];
    if (!entry) throw new Error(`unknown-topic: ${topic}`);
    return entry;
  }

  return {
    get: async (topic: string) => {
      // Push-only: an emit not listened to is gone, so there is nothing to get.
      if (realtimeName(topic)) return null;
      const entry = entryFor(topic);
      if (entry.isEmpty(store.selectSnapshot(entry.selector))) {
        const fetch$ = entry.refresh(store);
        // ngxs dispatch observables complete without emitting once handlers finish
        if (fetch$) await lastValueFrom(fetch$, { defaultValue: undefined });
      }
      return store.selectSnapshot(entry.selector);
    },
    subscribe: (topic: string, push: (payload: unknown) => void) => {
      const name = realtimeName(topic);
      if (name) return functionEvents!.onFunctionEvent(name, (data) => push({ name, data }));
      const entry = entryFor(topic);
      const subscription = store.select(entry.selector).subscribe(push);
      return () => subscription.unsubscribe();
    },
  };
}
